import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";

import { obfuscationRequests } from "@/api/(requests)/obfuscation";

import Captcha from "../Captcha/Captcha";
import {
  displayElavonCreditCardModal,
  parsePaymentResponse,
} from "../../../utils/PaymentProvider/elavon";
import { getPatientProfile, updateJWT } from "../../../redux/actions/auth";
import { getQueryParams } from "../../../utils/filters";
import { putPatientCardInfo } from "../../../redux/actions/patientCardInfo";

const MobileAddPayment = ({
  auth,
  dispatch,

  getPatientProfile,
  location,

  profile,
}) => {
  // For non-mobile testing
  if (!window.ReactNativeWebView) {
    window.ReactNativeWebView = {
      postMessage: () => {},
    };
  }

  const [modalShown, setModalShown] = useState(false);
  const [captchaVerificationToken, setCaptchaVerificationToken] = useState(false);

  const clearLocalStorage = () => {
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("refreshToken");
    window.localStorage.removeItem("accessToken");
  };

  const onBackendError = (response) => {
    clearLocalStorage();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        error: true,
        message: "API backend error for '/payment/method' endpoint",
        response,
        type: "apti_api_error",
      }),
    );
  };
  const onError = (error) => {
    clearLocalStorage();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        error: true,
        message: "Error",
        response: error,
        type: "modal_error",
      }),
    );
  };
  const onCancelled = () => {
    clearLocalStorage();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        error: true,
        message: "Elavon modal cancelled",
        type: "modal_cancelled",
      }),
    );
  };
  const onDeclined = (response) => {
    clearLocalStorage();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        error: true,
        message: "Declined",
        response,
        type: "card_declined",
      }),
    );
  };
  const onApproval = (response) => {
    const { username } = profile;

    dispatch(
      putPatientCardInfo({
        username: username,
        ...parsePaymentResponse(response),
        finallyCallback: () => {
          clearLocalStorage();
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              error: false,
              message: "Success!",
              type: "success",
            }),
          );
        },
        onErrorCallback: onBackendError,
      }),
    );
  };

  const handleElavonCreditCardModalException = (e) => {
    if (!e?.code) {
      // Treat any empty error codes as unknown server errors (such as Cloudflare 429s)
      clearLocalStorage();
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          error: true,
          error_message: "Unexpected error adding card on file. Please try again later.",
          message: "API backend server error for '/payment/session_token' endpoint",
          type: "apti_api_server_error",
        }),
      );
    } else if (e?.code >= 500) {
      // Allow user to temporarily progress if error is outside their control (server error)
      clearLocalStorage();
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          error: true,
          error_message: e.message,
          message: "API backend server error for '/payment/session_token' endpoint",
          type: "apti_api_server_error",
        }),
      );
    } else if (e?.code == 400 && e?.type === "ActivityException") {
      clearLocalStorage();
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          error: true,
          error_message: e.message,
          message: e.message,
          type: e.type,
        }),
      );
    }
    throw e;
  };

  const displayLightBox = async () => {
    const { first_name, last_name, username } = profile;

    try {
      await displayElavonCreditCardModal(
        dispatch,
        username,
        first_name,
        last_name,
        () => {}, // setErrorMessage
        "MOBILE",
        onError,
        onCancelled,
        onDeclined,
        onApproval,
        false,
        captchaVerificationToken,
      );
    } catch (e) {
      handleElavonCreditCardModalException(e);
    }
  };

  useEffect(() => {
    if (profile && !modalShown && captchaVerificationToken) {
      displayLightBox();
      setModalShown(true);
    }
  }, [captchaVerificationToken]);

  useEffect(() => {
    if (auth.token) {
      getPatientProfile();
    }
  }, [auth.token]);

  useEffect(() => {
    const getJWTFromObfuscationId = async () => {
      try {
        const id = getQueryParams("id", location.search);
        const username = getQueryParams("username", location.search);

        const deobfuscatedJWTResponse = await obfuscationRequests.obfuscationGetWithUsername({
          data: { id, username },
        });

        const { accessToken, refreshToken, token } = deobfuscatedJWTResponse.data;

        window.localStorage.setItem("token", token);
        window.localStorage.setItem("refreshToken", refreshToken);
        window.localStorage.setItem("accessToken", accessToken);
        dispatch(
          updateJWT({
            accessToken,
            refreshToken,
            token,
          }),
        );
      } catch (error) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            error: true,
            message: "API backend error for '/obfuscation/get_with_username' endpoint",
            response: error,
            type: "apti_api_error",
          }),
        );
      }
    };

    getJWTFromObfuscationId();
  }, []);

  return <Captcha setCaptchaVerificationToken={setCaptchaVerificationToken} />;
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  profile: state.auth.profile,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  ...bindActionCreators({ getPatientProfile }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MobileAddPayment));
